import React from 'react';

import type { UUID } from '@aline/common';
import { link } from '@aline/routes';
import { CircleDashed } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { Apollo } from 'src/gql/generated';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';
import { makeStyles } from 'tss-react/mui';

import {
  INTAKE_FORM_TITLE_INPUT_PLACEHOLDER,
  INTAKE_FORM_TITLE_TEXT_PLACEHOLDER,
} from './intake-form-fields';
import { IntakeCardLead } from './IntakeCardLead';
import { PhaseStatusIndicator } from '../phases/PhaseStatusIndicator';
import { useSidePanelView } from '../side-panel/use-side-panel-view';
import { useWorkflowDetailLinks } from '../WorkflowDetailContext';

const useStyles = makeStyles()(theme => ({
  card: {
    '&:hover': {
      borderColor: theme.appColors.primary,
      boxShadow: '0px 1px 8.2px -2px rgba(0, 0, 0, 0.15)',
    },
    alignItems: 'center',
    background: theme.palette.common.white,
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5, 2),
    transition: 'border-color 150ms, box-shadow 150ms',
    width: '100%',
  },
  incompleteBadge: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    fontSize: 12,
    fontWeight: 'bold',
    gap: 2,
  },
  statusActions: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(0.5),
  },
  statusSpacer: {
    flexShrink: 0,
    width: 30,
  },
  title: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface IntakeFormInstanceCardProps {
  intakeForm: ApolloTypes.IntakeFormFullFragment;
  workflowId: UUID;
}

export function IntakeFormInstanceCard({ intakeForm, workflowId }: IntakeFormInstanceCardProps) {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const { view } = useSidePanelView();
  const { openDrawer } = useGlobalDrawer();
  const links = useWorkflowDetailLinks(workflowId);
  const isComplete = Boolean(intakeForm.completedAt);
  const [updateIntakeForm] = Apollo.useUpdateIntakeFormValuesMutation();

  /**
   * The whole row toggles the fill/edit drawer: open it, or close back to AI if
   * it's already showing (matching the summary cards). The title pencil edits the
   * title in place and stops propagation so it doesn't also toggle the drawer.
   */
  const openFill = () => {
    if (view === 'intake') {
      navigate(links.baseUrl());
      return;
    }
    openDrawer();
    navigate(link.WorkflowDetailTabIntake({ workflowId }));
  };

  return (
    <div className={classes.card} onClick={openFill}>
      <IntakeCardLead>
        <InlineEditField
          classes={{ display: classes.title }}
          disabled={isComplete}
          editOnDisplayClick={false}
          iconSize={16}
          placeholder={INTAKE_FORM_TITLE_INPUT_PLACEHOLDER}
          textPlaceholder={INTAKE_FORM_TITLE_TEXT_PLACEHOLDER}
          value={intakeForm.title}
          onSave={title => updateIntakeForm({ variables: { input: { title, workflowId } } })}
        />
      </IntakeCardLead>
      <div className={classes.statusActions} onClick={e => e.stopPropagation()}>
        {isComplete ? (
          <PhaseStatusIndicator isCompleted isActive={false} />
        ) : (
          <span className={classes.incompleteBadge}>
            <CircleDashed size={16} />
            Incomplete
          </span>
        )}
        <div aria-hidden className={classes.statusSpacer} />
      </div>
    </div>
  );
}
